import { useState, useEffect } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import TableHeader from "../../components/offices/TableHeader";
import Modal from "../../components/modal/Modal";
import OfficeForm from "../../components/offices/OfficeForm";
import { useQuery } from "@apollo/client";
import {
  GET_COMPANY_OFFICES,
  GET_COMPANY_USERS,
} from "../../graphql/companyQueries";
import { GET_ALL_COMPANY_OFFICES } from "../../graphql/officeQueries";
import { formatPhoneNumber } from "../../utils/phoneFormat";
import { useParams } from "react-router-dom";
import PrimaryOfficeAction from "../../components/offices/PrimaryOfficeAction";
import Toast from "../../components/Toast/Toast";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { Table } from "antd";
import useDebounce from "../../hooks/useDebounce";

const Offices = () => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });
  const [showModal, setShowModal] = useState(false);
  const [agentsModal, setAgentsModal] = useState(false);
  const [formType, setFormType] = useState("add");
  const [offices, setOffices] = useState([]);
  const [agents, setAgents] = useState([]);
  const [office, setOffice] = useState({});
  const [searchText, setSearchText] = useState("");

  const debounceValue = useDebounce(searchText, 500);

  const { companyId } = useParams();

  const { loading, error, data, refetch } = useQuery(GET_ALL_COMPANY_OFFICES, {
    variables: {
      companyId,
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
      },
      searchText: debounceValue,
    },
    fetchPolicy: "no-cache",
  });

  const { data: users } = useQuery(GET_COMPANY_USERS, {
    variables: { companyId },
    fetchPolicy: "no-cache",
    onError(err) {
      Toast(
        "error",
        "There was an error loading the users. If the issue persists please contact the administrators"
      );
    },
  });

  const onChange = (pagination) => {
    setPagination((prev) => ({
      ...prev,
      page: pagination?.current,
      limit: pagination?.pageSize,
    }));
  };

  const columns = [
    {
      title: "Office Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <span className="font-medium text-black">{text}</span>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (_, record) => (
        <div className="font-medium text-black">
          {record.address && <div>{`${record.address},`}</div>}
          <div>{`${record.city}, ${record.state}, ${record.zip}`}</div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      align: "center",
      render: (phone) => (
        <span className="font-medium text-black">
          {formatPhoneNumber(phone)}
        </span>
      ),
    },
    {
      title: "Primary Office",
      dataIndex: "isDefault",
      key: "isDefault",
      align: "center",
      render: (isDefault) => (
        <div className="flex justify-center">
          <PrimaryOfficeAction isDefault={isDefault} />
        </div>
      ),
    },
    {
      title: "Agents",
      key: "agents",
      align: "center",
      render: (_, record) => (
        <div className="flex font-medium text-black justify-center">
          <button
            onClick={() => {
              setAgents(
                users?.getCompanyUsers.filter(
                  (user) => user.defaultOffice?.name === record.name
                )
              );
              setAgentsModal(true);
            }}
            className="flex space-x-2 items-center bg-primary text-white rounded-full px-4 py-1 hover:opacity-90"
          >
            View
          </button>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      skeleton: "actions",
      align: "center",
      render: (_, record) => (
        <div className="flex justify-center align-center space-x-3.5">
          <button
            className="hover:text-primary"
            onClick={() => handleEdit(record)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-5 h-5"
            >
              <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
              <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (data?.getAllCompanyOffice?.offices)
      setOffices(data.getAllCompanyOffice?.offices);
    setPagination((prev) => ({
      ...prev,
      totalCount: data?.getAllCompanyOffice?.totalCount,
    }));
  }, [data]);

  const handleEdit = (editOffice) => {
    setOffice(editOffice);
    setFormType("edit");
    setShowModal(true);
  };
  const handleModalRender = () => {
    setFormType("add");
    setShowModal(true);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleClose = () => {
    setShowModal(false);
    setOffice({});
  };

  const firstLoad = loading && !offices.length;

  return (
    <div id="companies">
      <Breadcrumb pageName="Offices" />
      <div className="py-6">
        <Card>
          <div className="m-4">
            <div className="flex justify-between  items-center">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-lg">
                  All Offices ({pagination.totalCount || 0})
                </span>
                <div className="relative">
                  <div className="absolute top-1/2 left-1 -translate-y-1/2">
                    <svg
                      className="fill-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.16666 3.33332C5.945 3.33332 3.33332 5.945 3.33332 9.16666C3.33332 12.3883 5.945 15 9.16666 15C12.3883 15 15 12.3883 15 9.16666C15 5.945 12.3883 3.33332 9.16666 3.33332ZM1.66666 9.16666C1.66666 5.02452 5.02452 1.66666 9.16666 1.66666C13.3088 1.66666 16.6667 5.02452 16.6667 9.16666C16.6667 13.3088 13.3088 16.6667 9.16666 16.6667C5.02452 16.6667 1.66666 13.3088 1.66666 9.16666Z"
                        fill=""
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.2857 13.2857C13.6112 12.9603 14.1388 12.9603 14.4642 13.2857L18.0892 16.9107C18.4147 17.2362 18.4147 17.7638 18.0892 18.0892C17.7638 18.4147 17.2362 18.4147 16.9107 18.0892L13.2857 14.4642C12.9603 14.1388 12.9603 13.6112 13.2857 13.2857Z"
                        fill=""
                      />
                    </svg>
                  </div>

                  <input
                    type="text"
                    placeholder="Type to search..."
                    className="w-full rounded-md border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none "
                    onChange={handleSearch}
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleModalRender}
                  className="flex space-x-2 items-center bg-primary text-white rounded-sm px-4 py-1 hover:opacity-90"
                >
                  <svg
                    className="fill-current"
                    width="20"
                    height="20"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                  </svg>
                  <span>Add an office</span>
                </button>
              </div>
            </div>
          </div>
          {error && !offices.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <Table
                bordered
                rowKey="_id"
                onChange={onChange}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : {
                      columns,
                      dataSource: offices,
                      pagination: {
                        position: ["bottomCenter"],
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.totalCount,
                      },
                    })}
                locale={{
                  emptyText: searchText ? (
                    <EmptyState
                      title="No offices match your search"
                      description={`Nothing found for "${searchText}".`}
                      actionLabel="Clear search"
                      onAction={() => setSearchText("")}
                    />
                  ) : (
                    <EmptyState
                      title="No offices yet"
                      description="Offices you add for this company will appear here."
                    />
                  ),
                }}
              />
            </>
          )}
        </Card>

        <Modal
          title={formType === "add" ? "Add Office" : "Edit Office"}
          showModal={showModal}
          setShowModal={(value) => {
            setShowModal(value);
            if (!value) setOffice({});
          }}
        >
          <OfficeForm
            office={office}
            companyId={companyId}
            formType={formType}
            handleClose={handleClose}
            allOffices={offices.map((o) => ({ id: o._id, name: o.name }))}
            refetchOffices={refetch}
          />
        </Modal>
        <Modal
          title="Assigned Agents"
          showModal={agentsModal}
          setShowModal={(value) => {
            setAgentsModal(value);
            setAgents([]);
          }}
        >
          {agents?.length ? (
            <ul>
              {agents?.map((agent, index) => (
                <li key={agent.email}>
                  {index + 1}. {agent.firstName} {agent.lastName} -{" "}
                  {agent.email}
                </li>
              ))}
            </ul>
          ) : (
            <p>No agents assigned.</p>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Offices;
